import React from 'react';

/**
 * How this talent came to be on Droomwork.
 *
 * Most rows are a marketing channel - the signup form records whichever
 * one brought them to the site. Two other cases read differently and are
 * worth telling apart at a glance:
 *
 *   - they clicked Apply on a job, in which case the job is the answer,
 *     with the company under it so two postings of "Sales Manager" from
 *     different clients are not confused with each other;
 *   - an admin registered them, which is not a channel at all;
 *   - a partner's referral link brought them (users.partner_id), shown as
 *     "Partner: <name>", or "<partner> -> <job title>" when they also came
 *     to apply for a job.
 */

/** The sources that mean somebody else created the account. */
const ADMIN_CREATED_SOURCES = ['admin_import', 'partner_import'];

/** A stored source turned into something an admin would say out loud. */
export function sourceLabel(source) {
  if (!source) {
    return 'Unknown';
  }
  if (source === 'droomwork') {
    return 'Droomwork';
  }
  if (source === 'job_application') {
    return 'Job application';
  }
  if (source === 'admin_import') {
    return 'Added by admin';
  }
  if (source === 'partner_import') {
    return 'Added by partner';
  }
  if (source === 'partner_referral') {
    return 'Partner referral';
  }

  // The marketing channels are stored lower-cased: twitter, linkedin...
  return source.charAt(0).toUpperCase() + source.slice(1);
}

const RegisteredVia = ({ talent }) => {
  if (!talent) {
    return <p className='text-muted-foreground'>—</p>;
  }

  const job = talent.registered_for_job;
  const partner = talent.registered_via_partner;

  /* A partner brought them. With a job as well, both read as one trail:
     the partner, then the job they came to apply for. */
  if (partner) {
    return (
      <div>
        <p className='text-sm text-foreground'>
          Partner: {partner.name}
          {job ? <span className='text-muted-foreground'>{' → '}{job.jobTitle || 'A job'}</span> : null}
        </p>
        {job && job.companyName ? (
          <p className='text-xs text-muted-foreground'>{job.companyName}</p>
        ) : null}
      </div>
    );
  }

  /* A job they applied for: the posting is the useful answer, and the
     company underneath is what separates one "Sales Manager" from
     another. */
  if (job) {
    return (
      <div>
        <p className='text-sm text-foreground'>{job.jobTitle || 'A job'}</p>
        {job.companyName ? (
          <p className='text-xs text-muted-foreground'>{job.companyName}</p>
        ) : null}
      </div>
    );
  }

  if (ADMIN_CREATED_SOURCES.indexOf(talent.registration_source) !== -1) {
    return (
      <div>
        <p className='text-sm text-foreground'>{sourceLabel(talent.registration_source)}</p>
        <p className='text-xs text-muted-foreground'>Not a self signup</p>
      </div>
    );
  }

  return <p className='text-sm text-foreground'>{sourceLabel(talent.registration_source)}</p>;
};

export default RegisteredVia;
